import React, { useState, useEffect } from 'react';
import { notificationService } from '../../../services/notificationService';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';

const NotificationLogsPanel = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    notificationType: '',
    status: '',
  });

  useEffect(() => {
    loadLogs();
  }, [filters]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const data = await notificationService?.getNotificationLogs(filters);
      setLogs(data);
    } catch (error) {
      console.error('Error loading notification logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      sent: 'bg-success/10 text-success',
      failed: 'bg-error/10 text-error',
      pending: 'bg-warning/10 text-warning',
    };
    const labels = {
      sent: 'Envoyé',
      failed: 'Échec',
      pending: 'En attente',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles?.[status] || 'bg-muted text-muted-foreground'}`}>
        {labels?.[status] || status}
      </span>
    );
  };

  const getTypeLabel = (type) => {
    const labels = {
      inventory_alert: 'Alerte Stock',
      schedule_assignment: 'Assignation Mission',
      schedule_reminder: 'Rappel Mission',
      urgent_alert: 'Alerte Urgente',
      general: 'Général',
    };
    return labels?.[type] || type;
  };

  const getTypeIcon = (type) => {
    const icons = {
      inventory_alert: 'Package',
      schedule_assignment: 'Calendar',
      schedule_reminder: 'Clock',
      urgent_alert: 'AlertTriangle',
      general: 'Mail',
    };
    return icons?.[type] || 'Mail';
  };

  return (
    <div>
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Select
          label="Type de notification"
          value={filters?.notificationType}
          onChange={(e) => setFilters({ ...filters, notificationType: e?.target?.value })}
        >
          <option value="">Tous les types</option>
          <option value="inventory_alert">Alerte Stock</option>
          <option value="schedule_assignment">Assignation Mission</option>
          <option value="schedule_reminder">Rappel Mission</option>
          <option value="urgent_alert">Alerte Urgente</option>
          <option value="general">Général</option>
        </Select>

        <Select
          label="Statut"
          value={filters?.status}
          onChange={(e) => setFilters({ ...filters, status: e?.target?.value })}
        >
          <option value="">Tous les statuts</option>
          <option value="sent">Envoyé</option>
          <option value="failed">Échec</option>
          <option value="pending">En attente</option>
        </Select>
      </div>
      {/* Logs List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : logs?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Aucune notification trouvée</p>
        </div>
      ) : (
        <div className="space-y-3">
          {logs?.map((log) => (
            <div
              key={log?.id}
              className="bg-muted/30 rounded-lg p-4 border border-border hover:border-primary/50 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name={getTypeIcon(log?.notificationType)} size={20} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-foreground">{log?.subject}</span>
                        {getStatusBadge(log?.status)}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {getTypeLabel(log?.notificationType)} • {log?.recipientEmail}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(log?.createdAt)?.toLocaleString('fr-FR')}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2">{log?.message}</p>
                  {log?.errorMessage && (
                    <div className="mt-2 p-2 bg-error/10 rounded text-xs text-error">
                      <strong>Erreur:</strong> {log?.errorMessage}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationLogsPanel;